/**
 * Persistencia del progreso de subida en IndexedDB.
 *
 * Es lo que convierte una subida de 2 horas en algo usable: si el navegador se
 * cierra, el móvil suspende la pestaña o se va la luz, al volver se reanuda
 * desde la última parte confirmada en vez de empezar de cero.
 *
 * Criterio de aceptación de F1, no un refinamiento (PLAN.md §12.4).
 *
 * Por qué IndexedDB y no localStorage: localStorage es síncrono (bloquea el
 * hilo principal en cada escritura, y aquí escribimos por cada parte) y tiene
 * un límite de ~5 MB compartido.
 */

const DB_NAME = 'chasquitambo-uploads'
const DB_VERSION = 1
const STORE = 'sessions'

export interface PersistedPart {
  partNumber: number
  etag: string
}

export interface PersistedSession {
  sessionId: string
  assetId: string
  projectId: string
  filename: string
  fileSize: number
  /** Se compara al reanudar: si no coincide, el usuario eligió otro archivo. */
  fileLastModified: number
  partSize: number
  totalParts: number
  parts: PersistedPart[]
  updatedAt: number
}

let dbPromise: Promise<IDBDatabase> | null = null

function open(): Promise<IDBDatabase> {
  dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'sessionId' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(asError(request.error, 'No se pudo abrir IndexedDB'))
  })

  return dbPromise
}

async function tx<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open()

  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(STORE, mode)
    const request = work(transaction.objectStore(STORE))

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(asError(request.error, 'Fallo al acceder a IndexedDB'))
  })
}

/**
 * IndexedDB expone el error como `DOMException | null`. Rechazar con `null`
 * produce un catch que no dice nada; se normaliza a Error para que la traza
 * sea legible cuando falle una reanudación.
 */
function asError(error: DOMException | null, fallback: string): Error {
  return error ?? new Error(fallback)
}

export async function saveSession(session: PersistedSession): Promise<void> {
  await tx('readwrite', (store) => store.put({ ...session, updatedAt: Date.now() }))
}

export async function loadSession(sessionId: string): Promise<PersistedSession | undefined> {
  return tx('readonly', (store) => store.get(sessionId) as IDBRequest<PersistedSession | undefined>)
}

export async function listSessions(): Promise<PersistedSession[]> {
  return tx('readonly', (store) => store.getAll() as IDBRequest<PersistedSession[]>)
}

export async function deleteSession(sessionId: string): Promise<void> {
  await tx('readwrite', (store) => store.delete(sessionId))
}

/**
 * Registra una parte confirmada.
 *
 * Se llama tras CADA parte. Con partes de 8 MiB, lo máximo que se puede perder
 * ante un corte son ~8 MiB de trabajo. Es la razón de no subir el tamaño de
 * parte "para optimizar": en un enlace lento, partes de 64 MiB significarían
 * perder un minuto en vez de seis segundos.
 */
export async function recordPart(sessionId: string, part: PersistedPart): Promise<void> {
  const session = await loadSession(sessionId)
  if (!session) return

  const others = session.parts.filter((p) => p.partNumber !== part.partNumber)
  await saveSession({ ...session, parts: [...others, part] })
}

/**
 * Limpia sesiones que ya no sirven.
 *
 * Quien mata el multipart es NUESTRO backend, no R2. La sesión caduca a las 6 h
 * (`UPLOAD_MULTIPART_TTL`) y `uploads:abort-stale` corre cada hora, así que a
 * las 7 h como mucho el `upload_id` ya está abortado y reanudar es imposible.
 *
 * La regla de lifecycle de R2 —abortar multiparts incompletos a los 7 días— es
 * sólo la red de seguridad por si el comando no corriera: siete días de partes
 * facturándose y de cuota bloqueada son demasiados para dejarlo en sus manos.
 * El mismo razonamiento está escrito en `AbortStaleUploadsService`.
 *
 * Las 24 h de aquí son, por tanto, holgura de sobra: sólo hay que barrer lo que
 * el backend ya abortó, y no hay prisa por hacerlo.
 */
export async function pruneStale(maxAgeMs = 24 * 60 * 60 * 1000): Promise<number> {
  const sessions = await listSessions()
  const cutoff = Date.now() - maxAgeMs
  const stale = sessions.filter((s) => s.updatedAt < cutoff)

  await Promise.all(stale.map((s) => deleteSession(s.sessionId)))

  return stale.length
}

/**
 * ¿Se puede reanudar esta sesión con este archivo?
 *
 * El navegador no conserva el File entre recargas: el usuario tiene que volver
 * a seleccionarlo. Comprobamos que sea el mismo antes de reutilizar los ETags,
 * o produciríamos un archivo corrupto mezclando partes de dos ficheros.
 */
export function matchesFile(session: PersistedSession, file: File): boolean {
  return session.fileSize === file.size && session.fileLastModified === file.lastModified
}
